/**
 * Phase 16F notification panel (REQ-NTF-01, 04, 13, 14, 15).
 * Tabs New / Acknowledged; filters Cleared (7 days) and Resolved (7 days). Rows grouped by patient
 * (the name only when the API supplies it), newest first, with Open / Acknowledge / Clear / Undo /
 * Restore. Bulk Acknowledge all and Clear all confirm with the count. Every action is per user.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { AlertTriangle, Check, ExternalLink, Loader, RotateCcw, Trash2, Undo2, X, WifiOff } from 'lucide-react';
import { useNotifications } from '../../context/NotificationContext';
import type { NotificationTab } from '../../dtos/notifications/notification.dto';
import type { NotificationItem } from '../../dtos/notifications/notification.mapper';
import type { NotificationBulkAction } from '../../services/notifications/notification.service';
import { useToast } from '../common/Toast';
import HospitalizationTaskDialog from './HospitalizationTaskDialog';

export const UNDO_WINDOW_MS = 24 * 60 * 60 * 1000;

const TAB_LABELS: Record<NotificationTab, string> = {
  new: 'New',
  acknowledged: 'Acknowledged',
  cleared: 'Cleared (7 days)',
  resolved: 'Resolved (7 days)',
};

const EMPTY_TEXT: Record<NotificationTab, string> = {
  new: 'No new critical alerts.',
  acknowledged: 'Nothing acknowledged and still open.',
  cleared: 'Nothing cleared in the last 7 days.',
  resolved: 'Nothing resolved in the last 7 days.',
};

const ACTION_DONE: Record<NotificationBulkAction, string> = {
  acknowledge: 'Acknowledged',
  unacknowledge: 'Moved back to New',
  clear: 'Cleared',
  restore: 'Restored',
};

/** "just now", "5 min ago", "3 h ago", "2 d ago". */
export function ageText(iso: string, now: number = Date.now()): string {
  const t = Date.parse(iso);
  if (!Number.isFinite(t)) return '';
  const minutes = Math.max(0, Math.floor((now - t) / 60000));
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} h ago`;
  return `${Math.floor(hours / 24)} d ago`;
}

export const canUndoAcknowledge = (item: NotificationItem, now: number = Date.now()): boolean => {
  if (item.state !== 'ACKNOWLEDGED' || !item.ackAt) return false;
  const t = Date.parse(item.ackAt);
  return Number.isFinite(t) && now - t <= UNDO_WINDOW_MS;
};

interface Group {
  key: string;
  heading: string;
  items: NotificationItem[];
}

/** Groups by patient (agency-level alerts last), newest group first, newest row first. */
export function groupByPatient(items: NotificationItem[]): Group[] {
  const sorted = [...items].sort((a, b) => Date.parse(b.escalatedAt || b.createdAt) - Date.parse(a.escalatedAt || a.createdAt));
  const groups = new Map<string, Group>();
  for (const item of sorted) {
    const key = item.patientId ? `p:${item.patientId}` : 'agency';
    let group = groups.get(key);
    if (!group) {
      group = { key, heading: item.patientId ? (item.patientName || 'Patient') : 'Agency', items: [] };
      groups.set(key, group);
    }
    if (item.patientName && group.heading === 'Patient') group.heading = item.patientName;
    group.items.push(item);
  }
  const list = Array.from(groups.values());
  return [...list.filter((g) => g.key !== 'agency'), ...list.filter((g) => g.key === 'agency')];
}

interface PendingBulk {
  action: Extract<NotificationBulkAction, 'acknowledge' | 'clear'>;
  ids: string[];
}

const NotificationPanel: React.FC<{ id: string; onClose: () => void }> = ({ id, onClose }) => {
  const ctx = useNotifications();
  const { showToast } = useToast();
  const headingRef = useRef<HTMLHeadingElement>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [pendingBulk, setPendingBulk] = useState<PendingBulk | null>(null);
  const [taskItem, setTaskItem] = useState<NotificationItem | null>(null);

  const tab = ctx?.activeTab ?? 'new';
  const list = ctx?.lists[tab];
  const loadList = ctx?.loadList;
  const needsLoad = !!list && !list.loaded && !list.loading && !list.error;

  useEffect(() => {
    headingRef.current?.focus();
  }, []);

  useEffect(() => {
    if (needsLoad && loadList) void loadList(tab);
  }, [needsLoad, loadList, tab]);

  const groups = useMemo(() => groupByPatient(list?.items || []), [list?.items]);

  if (!ctx || !list) return null;
  const items = list.items;
  const canUpdate = ctx.canUpdate;

  const selectTab = (next: NotificationTab) => {
    setPendingBulk(null);
    ctx.setActiveTab(next);
    void ctx.loadList(next);
  };

  const run = async (action: NotificationBulkAction, ids: string[], key: string) => {
    if (!ids.length) return;
    setBusy(key);
    try {
      const result = await ctx.act(action, ids);
      const done = result.updated.length;
      const skipped = result.skipped.length;
      const noun = done === 1 ? 'alert' : 'alerts';
      showToast(skipped ? `${ACTION_DONE[action]} ${done} ${noun}; ${skipped} could not be changed.` : `${ACTION_DONE[action]}${ids.length > 1 ? ` ${done} ${noun}` : ''}.`,
        { variant: skipped ? 'warning' : 'success' });
    } catch (err) {
      showToast(err instanceof Error && err.message ? err.message : 'Could not update notifications', { variant: 'error' });
    } finally {
      setBusy(null);
      setPendingBulk(null);
    }
  };

  const tabButton = (value: NotificationTab, extra?: string) => (
    <button
      key={value}
      type="button"
      role="tab"
      aria-selected={tab === value}
      aria-controls={`${id}-list`}
      onClick={() => selectTab(value)}
      className={`px-3 py-1.5 text-sm rounded-md whitespace-nowrap focus:outline-none focus-visible:ring-2 focus-visible:ring-slate-500 ${
        tab === value ? 'bg-slate-800 text-white' : 'text-slate-600 hover:bg-slate-100'
      }`}
    >
      {TAB_LABELS[value]}{extra}
    </button>
  );

  const newCount = ctx.count ?? 0;
  const bulkActions: Array<PendingBulk['action']> = tab === 'new' ? ['acknowledge', 'clear'] : tab === 'acknowledged' ? ['clear'] : [];

  return (
    <div
      id={id}
      role="dialog"
      aria-modal="false"
      aria-labelledby={`${id}-title`}
      className="fixed inset-x-2 top-16 bottom-2 sm:bottom-auto sm:absolute sm:inset-x-auto sm:right-0 sm:top-full sm:mt-2 sm:w-[28rem] sm:max-h-[75vh] bg-white border border-gray-200 rounded-lg shadow-xl z-50 flex flex-col text-left"
    >
      <div className="flex items-center justify-between px-4 pt-3 pb-2">
        <h2 id={`${id}-title`} ref={headingRef} tabIndex={-1} className="text-base font-semibold text-slate-900 focus:outline-none">
          Critical alerts
        </h2>
        <button type="button" onClick={onClose} className="p-2 rounded-md text-slate-500 hover:bg-slate-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-slate-500" aria-label="Close notifications">
          <X size={18} aria-hidden="true" />
        </button>
      </div>

      <div className="px-4 space-y-2">
        <div role="tablist" aria-label="Inbox" className="flex gap-1">
          {tabButton('new', newCount > 0 ? ` (${newCount})` : '')}
          {tabButton('acknowledged')}
        </div>
        <div role="tablist" aria-label="Recent" className="flex gap-1 overflow-x-auto">
          {tabButton('cleared')}
          {tabButton('resolved')}
        </div>
      </div>

      {ctx.status === 'closed' && (
        <p className="mx-4 mt-2 flex items-center gap-1.5 text-xs text-amber-700" role="status">
          <WifiOff size={14} aria-hidden="true" /> Live updates paused; checking every minute.
        </p>
      )}
      {ctx.deepLinkError && (
        <div className="mx-4 mt-2 flex items-start justify-between gap-2 rounded-md bg-amber-50 border border-amber-200 px-3 py-2 text-sm text-amber-800" role="alert">
          <span>{ctx.deepLinkError}</span>
          <button type="button" className="text-amber-800 underline" onClick={ctx.clearDeepLinkError}>Dismiss</button>
        </div>
      )}

      {canUpdate && bulkActions.length > 0 && items.length > 0 && (
        <div className="px-4 mt-2">
          {pendingBulk ? (
            <div className="flex flex-wrap items-center gap-2 rounded-md bg-slate-50 border border-slate-200 px-3 py-2" role="alertdialog" aria-label="Confirm bulk action">
              <span className="text-sm text-slate-800">
                {pendingBulk.action === 'acknowledge' ? 'Acknowledge' : 'Clear'} all {pendingBulk.ids.length} {pendingBulk.ids.length === 1 ? 'alert' : 'alerts'}?
              </span>
              <div className="ml-auto flex gap-2">
                <button type="button" className="px-3 py-1.5 text-sm rounded-md bg-gray-100 text-gray-700 hover:bg-gray-200" onClick={() => setPendingBulk(null)} disabled={busy !== null}>
                  Cancel
                </button>
                <button
                  type="button"
                  className="px-3 py-1.5 text-sm rounded-md bg-slate-800 text-white hover:bg-slate-700 disabled:opacity-60"
                  onClick={() => run(pendingBulk.action, pendingBulk.ids, 'bulk')}
                  disabled={busy !== null}
                >
                  {busy === 'bulk' ? 'Working…' : 'Confirm'}
                </button>
              </div>
            </div>
          ) : (
            <div className="flex gap-2">
              {bulkActions.map((action) => (
                <button
                  key={action}
                  type="button"
                  className="px-3 py-1.5 text-sm rounded-md bg-gray-100 text-gray-700 hover:bg-gray-200"
                  onClick={() => setPendingBulk({ action, ids: items.map((n) => n.id) })}
                  disabled={busy !== null}
                >
                  {action === 'acknowledge' ? 'Acknowledge all' : 'Clear all'}
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      <div id={`${id}-list`} role="tabpanel" aria-label={TAB_LABELS[tab]} className="mt-2 flex-1 overflow-y-auto px-4 pb-4" aria-busy={list.loading}>
        {list.loading && !items.length ? (
          <div className="flex items-center justify-center gap-2 py-10 text-sm text-slate-500" role="status">
            <Loader size={18} className="animate-spin" aria-hidden="true" /> Loading alerts
          </div>
        ) : list.error ? (
          <div className="py-8 text-center" role="alert">
            <p className="text-sm text-red-700 flex items-center justify-center gap-1.5"><AlertTriangle size={16} aria-hidden="true" /> {list.error}</p>
            <button type="button" className="mt-3 px-3 py-1.5 text-sm rounded-md bg-gray-100 text-gray-700 hover:bg-gray-200" onClick={() => ctx.loadList(tab)}>
              Try again
            </button>
          </div>
        ) : !items.length ? (
          <p className="py-10 text-center text-sm text-slate-500">{EMPTY_TEXT[tab]}</p>
        ) : (
          groups.map((group) => (
            <section key={group.key} aria-label={group.heading} className="mt-3 first:mt-1">
              <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1">{group.heading}</h3>
              <ul className="space-y-2">
                {group.items.map((item) => (
                  <NotificationRow
                    key={item.id}
                    item={item}
                    tab={tab}
                    busy={busy}
                    canUpdate={canUpdate}
                    onOpen={() => ctx.open(item)}
                    onAction={(action) => run(action, [item.id], `${action}:${item.id}`)}
                    onTask={() => setTaskItem(item)}
                  />
                ))}
              </ul>
            </section>
          ))
        )}
      </div>

      {taskItem && (
        <HospitalizationTaskDialog
          notificationId={taskItem.id}
          onClose={() => setTaskItem(null)}
          onSubmit={(task) => ctx.completeTask(taskItem.id, task)}
          onDone={() => {
            setTaskItem(null);
            showToast('Health plan notification recorded.', { variant: 'success' });
          }}
        />
      )}
    </div>
  );
};

interface RowProps {
  item: NotificationItem;
  tab: NotificationTab;
  busy: string | null;
  canUpdate: boolean;
  onOpen: () => void;
  onAction: (action: NotificationBulkAction) => void;
  onTask: () => void;
}

const actionBtn = 'inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium rounded-md focus:outline-none focus-visible:ring-2 focus-visible:ring-slate-500 disabled:opacity-50';

const NotificationRow: React.FC<RowProps> = ({ item, tab, busy, canUpdate, onOpen, onAction, onTask }) => {
  const disabled = busy !== null;
  const showTask = canUpdate && item.code === 'PATIENT_HOSPITALIZED' && item.task && !item.task.done && item.status === 'OPEN';
  return (
    <li className="rounded-md border border-gray-200 p-3" data-testid={`notification-row-${item.id}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-1.5">
            <span className="px-1.5 py-0.5 text-[11px] font-semibold rounded bg-red-50 text-red-700">{item.category}</span>
            {item.escalatedAt && item.status === 'OPEN' && (
              <span className="px-1.5 py-0.5 text-[11px] font-semibold rounded bg-amber-100 text-amber-800">Escalated</span>
            )}
            {item.status === 'RESOLVED' && (
              <span className="px-1.5 py-0.5 text-[11px] font-semibold rounded bg-green-100 text-green-800">Resolved</span>
            )}
          </div>
          <p className="mt-1 text-sm text-slate-900">{item.message}</p>
          {item.offlineHint && <p className="mt-0.5 text-xs text-slate-500">The visit may have started offline.</p>}
          {item.task && item.code === 'PATIENT_HOSPITALIZED' && (
            <p className="mt-0.5 text-xs text-slate-600">
              {item.task.done ? 'Health plan notified.' : 'Task: notify the health plan.'}
            </p>
          )}
        </div>
        <span className="shrink-0 text-xs text-slate-500">{ageText(item.escalatedAt || item.createdAt)}</span>
      </div>
      <div className="mt-2 flex flex-wrap gap-1.5">
        {item.target && (
          <button type="button" className={`${actionBtn} bg-slate-800 text-white hover:bg-slate-700`} onClick={onOpen}>
            <ExternalLink size={14} aria-hidden="true" /> Open
          </button>
        )}
        {showTask && (
          <button type="button" className={`${actionBtn} bg-blue-600 text-white hover:bg-blue-700`} onClick={onTask} disabled={disabled}>
            Mark health plan notified
          </button>
        )}
        {canUpdate && tab === 'new' && (
          <button type="button" className={`${actionBtn} bg-gray-100 text-gray-700 hover:bg-gray-200`} onClick={() => onAction('acknowledge')} disabled={disabled}>
            <Check size={14} aria-hidden="true" /> Acknowledge
          </button>
        )}
        {canUpdate && tab === 'acknowledged' && canUndoAcknowledge(item) && (
          <button type="button" className={`${actionBtn} bg-gray-100 text-gray-700 hover:bg-gray-200`} onClick={() => onAction('unacknowledge')} disabled={disabled}>
            <Undo2 size={14} aria-hidden="true" /> Undo
          </button>
        )}
        {canUpdate && (tab === 'new' || tab === 'acknowledged') && (
          <button type="button" className={`${actionBtn} bg-gray-100 text-gray-700 hover:bg-gray-200`} onClick={() => onAction('clear')} disabled={disabled}>
            <Trash2 size={14} aria-hidden="true" /> Clear
          </button>
        )}
        {canUpdate && tab === 'cleared' && item.status === 'OPEN' && (
          <button type="button" className={`${actionBtn} bg-gray-100 text-gray-700 hover:bg-gray-200`} onClick={() => onAction('restore')} disabled={disabled}>
            <RotateCcw size={14} aria-hidden="true" /> Restore
          </button>
        )}
      </div>
    </li>
  );
};

export default NotificationPanel;
